/**
 * Authentication store for Idle Depths.
 *
 * Manages authentication state using server-side guest tracking:
 * - Guest users are tracked server-side for analytics
 * - Session recovery from localStorage for returning guests
 * - Authenticated users have OAuth linked for cloud sync
 * - Handles Google OAuth flow
 */

import {
  type AuthStatus,
  type User,
  type AuthError,
  type GuestData,
  type SessionResponse,
} from '@idle-depths/shared';
import * as Sentry from '@sentry/react';
import { makeAutoObservable, runInAction } from 'mobx';

import type { RootStore } from './RootStore';

import { getVanillaClient, getApiBaseUrl } from '@/lib/trpc';
import { LocalStorageService } from '@/services/LocalStorageService';

/**
 * Authentication store.
 *
 * Manages user authentication state and provides methods for
 * login, logout, and guest data management.
 */
export class AuthStore {
  /** Current authentication status */
  status: AuthStatus = 'loading';

  /** Authenticated user (null if guest) */
  user: User | null = null;

  /** Authentication error (null if no error) */
  error: AuthError | null = null;

  /** Guest data from localStorage */
  guestData: GuestData | null = null;

  constructor(_rootStore: RootStore) {
    // rootStore is kept for future cross-store coordination
    makeAutoObservable(this);
  }

  /**
   * Initialize authentication state.
   *
   * Checks for existing session on server.
   * If session exists (guest or authenticated), sets appropriate status.
   * If no session but localStorage has guestUserId, attempts auto-recovery.
   * Otherwise, sets unauthenticated (user needs to choose login method).
   * Called once on app startup.
   */
  async initialize(): Promise<void> {
    try {
      const client = getVanillaClient();
      // Check server for existing session
      const result = await client.auth.getSession.query();

      if (result.user) {
        // Active session found
        const user = result.user;
        runInAction(() => {
          this.status = user.isGuest ? 'guest' : 'authenticated';
          this.user = user;
          this.error = null;
        });

        // Set Sentry user context
        Sentry.setUser({
          id: user.id,
          email: user.email ?? '',
        });

        return;
      }

      // No active session - check for recoverable guest
      const guestUserId = LocalStorageService.getGuestUserId();

      if (guestUserId) {
        // Attempt to recover guest session
        const recovered = await this.recoverGuestSession(guestUserId);

        if (recovered) {
          // Successfully recovered - done
          return;
        }

        // Recovery failed - clear stale guestUserId
        LocalStorageService.clearGuestUserId();
      }

      // No session and no recoverable guest
      runInAction(() => {
        this.status = 'unauthenticated';
        this.user = null;
      });
    } catch (error) {
      // Server error - allow guest mode as fallback
      runInAction(() => {
        this.status = 'unauthenticated';
        this.user = null;
        this.error = {
          code: 'network_error',
          message: 'Could not reach server. You can still play as guest.',
        };
      });

      Sentry.captureException(error, {
        tags: { context: 'auth_init' },
      });
    }
  }

  /**
   * Recover a guest session from the server.
   *
   * @param guestUserId - The server-side guest user ID
   * @returns True if recovery succeeded, false otherwise
   */
  private async recoverGuestSession(guestUserId: string): Promise<boolean> {
    try {
      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/auth/guest/recover`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ guestUserId }),
      });

      if (!response.ok) {
        // Recovery failed (GUEST_NOT_FOUND or GUEST_UPGRADED)
        return false;
      }

      const jsonData: unknown = await response.json();
      const result = jsonData as SessionResponse;

      runInAction(() => {
        this.status = 'guest';
        this.user = result.user;
        this.error = null;
      });

      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Guest session recovered',
        level: 'info',
      });

      return true;
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'auth_guest_recover' },
      });
      return false;
    }
  }

  /**
   * Continue as guest user.
   *
   * Called when user explicitly chooses to play as guest.
   * Creates a server-side guest account for analytics tracking.
   * Falls back to local-only guest if server is unavailable.
   */
  async continueAsGuest(): Promise<void> {
    // First, check if we have a stored guestUserId to recover
    const existingGuestUserId = LocalStorageService.getGuestUserId();

    if (existingGuestUserId) {
      // Try to recover existing guest session
      const recovered = await this.recoverGuestSession(existingGuestUserId);

      if (recovered) {
        return;
      }

      // Recovery failed - clear stale ID and create new guest
      LocalStorageService.clearGuestUserId();
    }

    // Create new server-side guest account
    try {
      const apiUrl = getApiBaseUrl();
      const response = await fetch(`${apiUrl}/auth/guest`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to create guest session');
      }

      const jsonData: unknown = await response.json();
      const result = jsonData as SessionResponse;

      // Store guest user ID for session recovery
      if (result.user) {
        LocalStorageService.setGuestUserId(result.user.id);
      }

      runInAction(() => {
        this.status = 'guest';
        this.user = result.user;
        this.guestData = LocalStorageService.getOrCreateGuestData();
        this.error = null;
      });

      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Server-side guest account created',
        level: 'info',
      });
    } catch (error) {
      // Server unavailable - fall back to local-only guest
      Sentry.captureException(error, {
        tags: { context: 'auth_guest_create' },
      });

      runInAction(() => {
        this.status = 'guest';
        this.user = null;
        this.guestData = LocalStorageService.getOrCreateGuestData();
        this.error = {
          code: 'network_error',
          message: 'Could not reach server. Playing offline.',
        };
      });

      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Local-only guest mode (server unavailable)',
        level: 'warning',
      });
    }

    // Clear Sentry user context for guests
    Sentry.setUser(null);
  }

  /**
   * Set status to unauthenticated (internal use).
   * Used after logout or account deletion.
   * User must explicitly choose to continue as guest or log in.
   *
   * @param clearGuestUserId - Whether to clear the guest user ID (default: true for authenticated users)
   */
  private setUnauthenticated(clearGuestUserId = true): void {
    this.status = 'unauthenticated';
    this.user = null;
    this.guestData = null;
    this.error = null;

    // Clear guest user ID if requested (authenticated users who log out)
    // Don't clear for guests - they should keep their ID for future recovery
    if (clearGuestUserId) {
      LocalStorageService.clearGuestUserId();
    }

    // Clear Sentry user context
    Sentry.setUser(null);
  }

  /**
   * Start Google OAuth login flow.
   *
   * Redirects to the server's Google OAuth endpoint.
   */
  loginWithGoogle(): void {
    // Get API URL based on environment
    const apiUrl = getApiBaseUrl();
    window.location.href = `${apiUrl}/auth/google`;
  }

  /**
   * Logout current user.
   *
   * Invalidates server session and reverts to unauthenticated state.
   * For guests: preserves guestUserId for future recovery.
   * For authenticated: clears guestUserId (they upgraded, old ID is stale).
   */
  async logout(): Promise<void> {
    const wasGuest = this.status === 'guest';

    if (this.status !== 'authenticated' && this.status !== 'guest') {
      return;
    }

    try {
      const client = getVanillaClient();
      await client.auth.logout.mutate();
    } catch (error) {
      // Log but don't fail - user should still be logged out locally
      Sentry.captureException(error, {
        tags: { context: 'auth_logout' },
      });
    }

    runInAction(() => {
      // Clear guestUserId only for authenticated users (they upgraded)
      // Guests keep their ID for future session recovery
      this.setUnauthenticated(!wasGuest);
    });

    Sentry.addBreadcrumb({
      category: 'auth',
      message: wasGuest ? 'Guest logged out' : 'User logged out',
      level: 'info',
    });
  }

  /**
   * Delete user account.
   *
   * Permanently deletes the account on the server and reverts to guest mode.
   */
  async deleteAccount(): Promise<{ success: boolean; error?: string }> {
    if (this.status !== 'authenticated') {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const client = getVanillaClient();
      await client.auth.deleteAccount.mutate();

      runInAction(() => {
        this.setUnauthenticated();
      });

      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Account deleted',
        level: 'info',
      });

      return { success: true };
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'auth_delete' },
      });

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to delete account',
      };
    }
  }

  /**
   * Import guest data to authenticated account.
   *
   * Called after first login to migrate guest progress.
   */
  async importGuestData(): Promise<{ success: boolean; error?: string }> {
    if (this.status !== 'authenticated' || !this.guestData) {
      return { success: false, error: 'No guest data to import' };
    }

    try {
      const client = getVanillaClient();
      await client.auth.importGuestData.mutate(this.guestData);

      // Clear guest data after successful import
      LocalStorageService.clearGuestData();

      runInAction(() => {
        this.guestData = null;
      });

      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Guest data imported',
        level: 'info',
      });

      return { success: true };
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'auth_import' },
      });

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to import guest data',
      };
    }
  }

  /**
   * Clear authentication error.
   */
  clearError(): void {
    this.error = null;
  }

  /**
   * Whether the user is fully authenticated (via OAuth).
   */
  get isAuthenticated(): boolean {
    return this.status === 'authenticated' && this.user !== null && !this.user.isGuest;
  }

  /**
   * Whether we have a server-side user (guest or authenticated).
   */
  get hasServerUser(): boolean {
    return (this.status === 'authenticated' || this.status === 'guest') && this.user !== null;
  }

  /**
   * Whether we should sync data to server.
   *
   * Sync when we have a server-side user (either guest or authenticated).
   */
  get shouldSyncToServer(): boolean {
    return this.hasServerUser;
  }

  /**
   * Whether there is guest data that can be imported.
   */
  get hasGuestData(): boolean {
    return this.guestData !== null && this.guestData.guestId.length > 0;
  }

  /**
   * Whether the user is a server-side guest.
   */
  get isGuest(): boolean {
    return this.status === 'guest' && this.user?.isGuest === true;
  }

  /**
   * Whether the authenticated user needs to provide consent.
   *
   * Returns true if:
   * - User is authenticated (not guest)
   * - User exists
   * - User has not accepted the privacy policy
   *
   * Guests don't require consent as they don't have accounts.
   */
  get needsConsent(): boolean {
    return this.isAuthenticated && this.user !== null && this.user.privacyAcceptedAt === null;
  }

  /**
   * Record user consent for privacy policy and terms of service.
   *
   * Called after user accepts the consent dialog.
   * Updates the server and local user state.
   *
   * @param consent - Consent data from the dialog
   */
  async recordConsent(consent: {
    analyticsConsent: boolean;
    marketingConsent: boolean;
  }): Promise<{ success: boolean; error?: string }> {
    if (!this.isAuthenticated || !this.user) {
      return { success: false, error: 'Not authenticated' };
    }

    try {
      const client = getVanillaClient();
      const result = await client.auth.recordConsent.mutate({
        analyticsConsent: consent.analyticsConsent,
        marketingConsent: consent.marketingConsent,
      });

      // Update local user state with consent timestamps
      runInAction(() => {
        if (this.user) {
          this.user.privacyAcceptedAt = result.privacyAcceptedAt;
          this.user.termsAcceptedAt = result.termsAcceptedAt;
        }
      });

      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'User consent recorded',
        level: 'info',
        data: {
          analyticsConsent: consent.analyticsConsent,
          marketingConsent: consent.marketingConsent,
        },
      });

      return { success: true };
    } catch (error) {
      Sentry.captureException(error, {
        tags: { context: 'auth_consent' },
      });

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to record consent',
      };
    }
  }

  /**
   * Reset store to initial state.
   */
  reset(): void {
    runInAction(() => {
      this.status = 'loading';
      this.user = null;
      this.error = null;
      this.guestData = null;
    });
  }
}
